import type { FC } from "react";
import {
  Button,
  CloseIcon,
  DeleteIcon,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Heading,
  IconButton,
  Row,
  useToast,
} from "@hightouch/htui";
import { Drawer } from "src/components/drawer";
import {
  useSaveAdArtifactMutation,
  useDeleteAdArtifactMutation,
} from "src/graphql";
import { useQueryClient } from "react-query";
import { ArtifactContent } from "./artifact-content";

export const AdArtifactFileDrawer: FC<{
  isOpen: boolean;
  onClose: () => void;
  content?: string;
  isLoading?: boolean;
  interactionId?: string;
  filename: string;
  artifactId?: string;
  onDelete?: () => void;
  trapFocus?: boolean;
}> = ({
  isOpen,
  onClose,
  content,
  isLoading,
  interactionId,
  filename,
  artifactId,
  onDelete,
  trapFocus,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const saveAdArtifactMutation = useSaveAdArtifactMutation();
  const deleteAdArtifactMutation = useDeleteAdArtifactMutation({
    onSuccess: () => {
      toast({
        id: "artifact-deleted",
        title: "Artifact deleted",
        variant: "success",
      });
      // Invalidate all workspace artifacts queries to refresh the table
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "WorkspaceAdArtifacts";
        },
      });
      onDelete?.();
      onClose();
    },
    onError: (error) => {
      toast({
        id: "artifact-delete-error",
        title: "Failed to delete artifact",
        message: error.message,
        variant: "error",
      });
    },
  });

  const displayName = filename ?? "Artifact";

  const handleSave = async () => {
    if (!interactionId) {
      return;
    }
    try {
      await saveAdArtifactMutation.mutateAsync({
        interactionId,
        path: filename,
      });
      toast({
        id: "artifact-saved",
        title: "Artifact saved",
        variant: "success",
      });
      // Close drawer on success
      onClose();
    } catch (error) {
      toast({
        id: "artifact-save-error",
        title: "Failed to save artifact",
        message: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
      console.error("Failed to save artifact:", error);
    }
  };

  const handleDelete = async () => {
    if (!artifactId) {
      return;
    }
    if (
      !confirm(
        "Are you sure you want to delete this artifact? This cannot be undone.",
      )
    ) {
      return;
    }
    await deleteAdArtifactMutation.mutateAsync({ id: artifactId });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg" trapFocus={trapFocus}>
      <DrawerHeader>
        <Row
          align="center"
          flex={1}
          minWidth={0}
          justifyContent="space-between"
        >
          <Heading>{displayName}</Heading>
          <Row align="center" gap={2}>
            {artifactId && (
              <IconButton
                aria-label="Delete artifact"
                icon={DeleteIcon}
                variant="danger"
                onClick={handleDelete}
                isLoading={deleteAdArtifactMutation.isLoading}
              />
            )}
            <IconButton
              aria-label="Close drawer"
              icon={CloseIcon}
              onClick={onClose}
            />
          </Row>
        </Row>
      </DrawerHeader>
      <DrawerBody p={0} display="flex" flexDirection="column" overflow="hidden">
        {isOpen && (
          <ArtifactContent
            filename={filename}
            interactionId={interactionId}
            content={content}
            isLoading={isLoading}
          />
        )}
      </DrawerBody>
      {interactionId && (
        <DrawerFooter>
          <Button
            onClick={handleSave}
            isLoading={saveAdArtifactMutation.isLoading}
            isDisabled={isLoading}
          >
            Save
          </Button>
        </DrawerFooter>
      )}
    </Drawer>
  );
};
